import React from 'react';
import './App.css';
import {GlobalContext} from './GlobalContext';
import Cookies from 'universal-cookie';
import {AppUI} from './components/AppUI';
import {LoginForm} from './components/LoginForm';
import {checkCache, putCache} from "./utils/Cache";
import {ConfirmModal} from "./components/common/ConfirmModal";
import moment from "moment-timezone";
import {closeWebSocket} from "./utils/WebSockets";
import ChangePasswordForm from "./components/user/ChangePasswordForm";
import {Card, Col, Container, Row} from "react-bootstrap";

const AUTO_LOGOUT_MINUTES = 30;

// Will fetch a new token if time till expiry is less than this number of minutes
const NEW_TOKEN_MINUTES = 20;

class App extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            authenticated: false,
            token: null,
            tokenExpiresAt: 0,
            userProfile: null,
            userDidAuthenticate: this.userDidAuthenticate,
            passwordExpired: false,
            userDidLogout: this.userDidLogout,
            apiRequest: this.apiRequest,
            basicApiRequest: this.basicApiRequest,
            resetLogoutTimer: this.resetLogoutTimer,
            refreshMyProfile: this.refreshMyProfile,
            cachedGet: this.cachedGet,
            toast: this.toast,
            hideToast: this.hideToast,
            showToast: false,
            toastInfo: {
                title: '',
                message: '',
                iconName: '',
                iconColor: ''
            },
            toastMessage: '',
            toastTitle: '',
            hasAnyRole: this.hasAnyRole,
            confirmConfig: {
                visible: false
            },
            confirm: this.confirm,
            loginMessage: ""
        };
    };

    confirm = (cb, prompt, title, okButtonText, cancelButtonText, size) => {
        let cfg = {
            prompt: prompt || 'Are you sure?',
            title: title || 'Confirm',
            callback: () => {
                cb();
                this.closeConfirm();
            },
            visible: false,
            okButtonText: okButtonText || "OK",
            cancelButtonText: cancelButtonText || "Cancel",
            size
        };
        this.setState({confirmConfig: cfg}, () => this.setState({confirmConfig: {...cfg, visible: true}}));
    };

    closeConfirm = () => {
        let cc = this.state.confirmConfig;
        this.setState({confirmConfig: {...cc, visible: false}});
    }

    hasAnyRole = (roles) => {
        if (roles && this.state.userProfile && this.state.userProfile.roles) {
            if (Array.isArray(roles)) {
                return (this.state.userProfile.roles.find(r => roles.includes(r)) != null);
            } else {
                return this.state.userProfile.roles.includes(roles);
            }
        }
        return false;
    };

    componentDidMount() {
        //check for token cookie on page load (in case of page refresh)
        this.refreshToken();
    }

    refreshToken = () => {
        let token = this.state.token;
        if (!token) {
            // Possible page refresh - check for cookie
            const cookies = new Cookies();
            let cookieVal = cookies.get("t2");
            let tokenExpiresAt = 0;
            if (cookieVal) {
                token = cookieVal.token;
                tokenExpiresAt = cookieVal.tokenExpiresAt;
            } else {
                // try the old version
                cookieVal = cookies.get("t");
                if (cookieVal) {
                    token = cookieVal;
                }
            }

            if (token) {
                // We read the token from the cookie - call the necessary
                // logic to make sure the app is in its authenticated state
                this.userDidAuthenticate(token, null, false, tokenExpiresAt);
            }
        }

        // No token present in state or cookie - logout
        if (!token) {
            this.userDidLogout();
        }
    };

    cachedGet = (uri, expirySeconds) => {
        let cached = checkCache(uri);
        if (cached) {
            return Promise.resolve(cached);
        } else {
            return this.apiRequest("GET", uri)
                .then(d => {
                    if (d) {
                        putCache(uri, d, expirySeconds || 60);
                    }
                    return Promise.resolve(d);
                });
        }
    }

    /**
     * Returns a Promise that will resolve to the response data.
     * @param command
     * @returns {Promise<Data>}
     */
    apiRequest = (method, uri, payload, dontResetLogoutTimer) => {
        const res = this.basicApiRequest(method, uri, payload, dontResetLogoutTimer)
            .then(this.json)
            .catch((err) => {
                console.log(err.message);
                if ("Access Denied" !== err.message) {
                    //internal
                    this.toast('Error', 'Oops! Something went wrong on our end.');
                    return Promise.resolve({});
                }
            })
            .finally(() => {
                if (!dontResetLogoutTimer) {
                    this.resetLogoutTimer();
                }
            });

        return res;
    };

    /**
     * Do a fetch without the standard post-processing, for rare cases where the UI code
     * wants to deal with the raw results of fetch().
     */
    basicApiRequest = (method, uri, payload, dontResetLogoutTimer) => {
        let settings = {
            method: method,
            headers: {
                "Accept": "application/json"
            }
        };
        if (this.state.token) {
            settings.headers['Authorization'] = "Bearer " + this.state.token;
            if (uri !== '/login' && !dontResetLogoutTimer) this.maybeGetNewToken();
        }
        if (payload) {
            settings.headers["Content-Type"] = "application/json";
            settings.body = JSON.stringify(payload);
        }
        return fetch("/web-api" + uri, settings)
            .then(this.checkStatus);
    }

    maybeGetNewToken = async () => {
        const now = Date.now();

        let minsTilExpiry = (this.state.tokenExpiresAt - now) / 60000;
        if (this.state.authenticated && this.state.token && minsTilExpiry <= NEW_TOKEN_MINUTES) {
            fetch("/web-api/login/refreshToken", {
                "method": "POST",
                "headers": {
                    "Accept": "application/json",
                    "Authorization": "Bearer " + this.state.token
                }
            })
            .then(this.checkStatus)
            .then(this.json)
            .then(resp => {
                this.userDidAuthenticate(resp.data.token, this.state.userProfile, false, resp.data.tokenExpiresAt);
            })
            .catch((err) => {
                console.log(err.message);
                if ("Access Denied" !== err.message) {
                    //internal
                    this.toast('Error', 'Oops! Something went wrong on our end.');
                    return Promise.resolve({});
                }
            });
        }

    }

    toast = (title, message, icon, color) => {
        this.setState({toastInfo: {title: title, message: message, iconName: icon, iconColor: color}, showToast: true});
    }

    hideToast = () => {
        this.setState({showToast: false});
    }

    json = (response) => {
        return response.json();
    };

    logoutAfter = Date.now() + (AUTO_LOGOUT_MINUTES*60000);
    logoutCheckInterval = null;

    resetLogoutTimer = () => {
        if (this.state.authenticated) {
            //trigger a logout in X min if nothing else is done
            this.logoutAfter = Date.now() + (AUTO_LOGOUT_MINUTES*60000);

            //set up the check interval if it doesn't exist already
            if (this.logoutCheckInterval) window.clearInterval(this.logoutCheckInterval);
            this.logoutCheckInterval = window.setInterval(this.maybeLogout, 5000);
        }
    };

    maybeLogout = () => {
        const now = Date.now();
        if (!this.state.authenticated || now >= this.logoutAfter) {
            this.userDidLogout();
        }
    }

    checkStatus = (response) => {
        //accept anything between 200-300, and 400 for 'bad input' responses
        if ((response.status >= 200 && response.status < 305) || response.status === 400) {
            return Promise.resolve(response);
        } else if (response.status === 403) {
            //log them out if access denied
            this.userDidLogout();
            return Promise.reject(new Error("Access Denied"));
        }
        return Promise.reject(new Error(response.statusText));
    };

    userDidAuthenticate = (t, userProfile, passwordExpired, tokenExpiresAt) => {
        const cookies = new Cookies();
        let expiry = tokenExpiresAt ? new Date(tokenExpiresAt) : moment.tz('UTC').add(AUTO_LOGOUT_MINUTES, 'minute').toDate();
        let cookieObj = {token: t, tokenExpiresAt: tokenExpiresAt || 0};
        cookies.set("t2", JSON.stringify(cookieObj), {path: "/", expires: expiry});
        this.setState({
            authenticated: true,
            token: t,
            tokenExpiresAt: tokenExpiresAt || 0,
            passwordExpired,
            loginMessage: ""
        }, () => {

            if (userProfile) {
                this.setState({userProfile}, this.resetLogoutTimer);
            } else {
                this.refreshMyProfile();
            }
        });
    };

    refreshMyProfile = () => {
        return this.apiRequest("GET", "/my/profile")
            .then(d => {
                if (d && d.data) {
                    this.setState({
                        userProfile: d.data
                    });
                }
            })
            .then(this.resetLogoutTimer);
    };

    userDidLogout = (msg) => {
        closeWebSocket();
        const cookies = new Cookies();
        cookies.remove("t");
        cookies.remove("t2");
        if (this.logoutCheckInterval) window.clearInterval(this.logoutCheckInterval);
        this.setState({
            authenticated: false,
            token: null,
            roles: null,
            searchTerm: "",
            userProfile: null,
            loginMessage: msg && typeof msg == "string" ? msg : ""
        });
    };

    render() {
        let cc = this.state.confirmConfig;
        return (
            <GlobalContext.Provider value={this.state}>
                <div className="App">
                    <ConfirmModal show={cc.visible} prompt={cc.prompt} title={cc.title} onOk={cc.callback} onCancel={this.closeConfirm} okLabel={cc.okButtonText} cancelLabel={cc.cancelButtonText} size={cc.size}/>
                    { this.state.authenticated && !this.state.passwordExpired && <AppUI/> }
                    { this.state.authenticated && this.state.passwordExpired && <Container
                        className={`mt-5 ${this.props.authenticated || this.props.signingUp ? "d-none" : ""}`}>
                        <Row>
                            <Col className="col-md-12 min-vh-100 d-flex flex-column justify-content-center">
                                <Row>
                                    <Col className="col-lg-6 col-md-8 mx-auto">
                                        <Card>
                                            <Card.Header>Password Expired</Card.Header>
                                            <Card.Body>
                                                <ChangePasswordForm onSuccess={() => this.userDidLogout("Please log in with your new password.")} onCancel={this.userDidLogout}/>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Container>}
                    { !this.state.authenticated && <LoginForm authenticated={this.state.authenticated}
                                  loginMessage={this.state.loginMessage}
                                     showSignup={() => this.setState({signingUp: true})}/> }
                </div>
            </GlobalContext.Provider>
        );
    };
}

export default App;
